import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { dateTime } from "@/lib/dates";
import {
  DndContext, DragOverlay, PointerSensor, KeyboardSensor, useSensor, useSensors,
  useDraggable, useDroppable, closestCorners,
} from "@dnd-kit/core";
import {
  ArrowLeft, ExternalLink, Table2, KanbanSquare, Plus, Search, Trash2, X, MessageSquare, Sparkles,
  ChevronDown, ChevronRight, Download, Loader2, Settings2,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import BoardCell from "@/components/BoardCell";
import ConfirmDialog from "@/components/ConfirmDialog";
import {
  COLUMN_TYPES, groupItems, kanbanFor, searchItems, toCsv, canEditSchema, groupProgress, cellText,
} from "@/components/boardsModel";

// A board: its schema (columns, groups) and its rows. Table is the primary
// view; Kanban is any status column turned sideways. Every edit is one PATCH
// and every PATCH is echoed back over the websocket to everyone else on the
// workspace, so two people on the same board see each other's cells change.

const pref = (k, d) => { try { return localStorage.getItem(k) ?? d; } catch { return d; } };
const remember = (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } };

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------
function EditableTitle({ value, onSave, canEdit, testId }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);
  if (!editing) {
    return (
      <button type="button" data-testid={testId} disabled={!canEdit} onClick={() => setEditing(true)}
        className="font-bold tracking-tight text-base text-left truncate max-w-[40vw] hover:text-brand disabled:hover:text-fg disabled:cursor-default">
        {value}
      </button>
    );
  }
  const commit = () => { setEditing(false); const v = draft.trim(); if (v && v !== value) onSave(v); };
  return <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { setDraft(value); setEditing(false); } }}
    className="font-bold text-base bg-surface border border-brand/50 px-2 py-0.5 outline-none" />;
}

// ---------------------------------------------------------------------------
// Table view
// ---------------------------------------------------------------------------
function AddItemRow({ groupId, onAdd, colSpan }) {
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const v = title.trim();
    if (!v || busy) return;
    setBusy(true);
    try { await onAdd(groupId, v); setTitle(""); } finally { setBusy(false); }
  };
  return (
    <tr>
      <td colSpan={colSpan} className="p-0">
        <form onSubmit={submit} className="flex items-center">
          <Plus className="w-3.5 h-3.5 text-fg/30 ml-2" />
          <input data-testid={`add-item-${groupId}`} value={title} onChange={(e) => setTitle(e.target.value)} disabled={busy}
            placeholder="+ Adaugă element" className="flex-1 px-2 py-1.5 text-xs bg-transparent outline-none placeholder-fg/30 focus:bg-surface-2" />
        </form>
      </td>
    </tr>
  );
}

function TableView({ board, items, users, canSchema, onCell, onTitle, onAdd, onOpenItem, onRenameGroup, onDeleteGroup, onAddGroup, onColumnMenu, activeId }) {
  const [collapsed, setCollapsed] = useState({});
  const sections = groupItems(board, items);
  const columns = board.columns.filter((c) => !c.hidden_in_table);
  const colSpan = columns.length + 2;
  const tableWidth = 340 + columns.reduce((sum, c) => sum + (c.width || 160), 0);
  return (
    <div className="overflow-auto h-full" data-testid="board-table">
      <table className="min-w-full table-fixed border-separate border-spacing-0 text-sm" style={{ width: tableWidth }}>
        <thead className="sticky top-0 z-10 bg-bg">
          <tr>
            <th style={{ width: 300 }} className="text-left text-[11px] font-semibold text-fg/50 px-3 py-2 border-b border-line/10 sticky left-0 bg-bg">Element</th>
            {columns.map((c) => (
              <th key={c.id} className="text-left text-[11px] font-semibold text-fg/50 px-2 py-2 border-b border-l border-line/10" style={{ width: c.width || 160 }}>
                <button type="button" disabled={!canSchema} onClick={() => onColumnMenu(c)} className="flex items-center gap-1 hover:text-fg disabled:cursor-default" title={canSchema ? "Editează coloana" : undefined}>
                  {c.label}{canSchema && <Settings2 className="w-3 h-3 opacity-40" />}
                </button>
              </th>
            ))}
            <th className="border-b border-l border-line/10 w-10" />
          </tr>
        </thead>
        {sections.map(({ group, items: rows }) => {
          const isCollapsed = !!collapsed[group.id];
          const prog = groupProgress(board, rows);
          return (
            <tbody key={group.id} data-testid={`group-${group.id}`}>
              <tr>
                <td colSpan={colSpan} className="pt-4 pb-1 px-2 sticky left-0 bg-bg">
                  <div className="flex items-center gap-2" style={{ color: group.color }}>
                    <button type="button" onClick={() => setCollapsed((c) => ({ ...c, [group.id]: !isCollapsed }))} aria-label={isCollapsed ? "Extinde" : "Restrânge"} className="p-0.5">
                      {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>
                    <EditableTitle value={group.label} canEdit={canSchema} onSave={(v) => onRenameGroup(group, v)} testId={`group-title-${group.id}`} />
                    <span className="text-[10px] mono opacity-70">{rows.length}</span>
                    {prog && <span className="text-[10px] mono opacity-70">· {prog.pct}% gata</span>}
                    {canSchema && board.groups.length > 1 && (
                      <button type="button" onClick={() => onDeleteGroup(group)} className="ml-1 p-0.5 opacity-40 hover:opacity-100" title="Șterge grupul (elementele se mută)"><Trash2 className="w-3 h-3" /></button>
                    )}
                  </div>
                </td>
              </tr>
              {!isCollapsed && rows.map((it) => (
                <tr key={it.id} data-testid={`item-row-${it.id}`} className={`group/row ${activeId === it.id ? "bg-brand/5" : "hover:bg-surface-2/40"}`}>
                  <td className="border-b border-line/10 sticky left-0 bg-bg p-0" style={{ borderLeft: `3px solid ${group.color}` }}>
                    <div className="flex items-center">
                      <div className="flex-1 min-w-0"><BoardCell column={{ type: "text" }} value={it.title} onChange={(v) => v.trim() && onTitle(it, v.trim())} /></div>
                      <button type="button" data-testid={`open-item-${it.id}`} onClick={() => onOpenItem(it.id)} className="px-2 py-1 text-fg/40 hover:text-brand flex items-center gap-1 text-[10px]" title="Deschide">
                        <MessageSquare className="w-3 h-3" /> Detalii{it.updates?.length > 0 && ` (${it.updates.length})`}
                      </button>
                    </div>
                  </td>
                  {columns.map((c) => (
                    <td key={c.id} className="border-b border-l border-line/10 p-0 h-9" data-testid={`cell-${it.id}-${c.id}`}>
                      <BoardCell column={c} value={it.values?.[c.id]} users={users} onChange={(v) => onCell(it, c, v)} />
                    </td>
                  ))}
                  <td className="border-b border-l border-line/10 p-0" />
                </tr>
              ))}
              {!isCollapsed && <AddItemRow groupId={group.id} onAdd={onAdd} colSpan={colSpan} />}
            </tbody>
          );
        })}
        {canSchema && (
          <tbody><tr><td colSpan={colSpan} className="pt-4 px-2">
            <button type="button" data-testid="add-group" onClick={onAddGroup} className="text-xs text-fg/50 hover:text-fg flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Grup nou</button>
          </td></tr></tbody>
        )}
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kanban view (any status column, sideways)
// ---------------------------------------------------------------------------
function KCard({ it, board, users, onOpen, overlay, dragging }) {
  const person = board.columns.find((c) => c.type === "person");
  const who = person ? cellText(person, it.values?.[person.id], users) : "";
  return (
    <div data-testid={overlay ? undefined : `kcard-${it.id}`}
      className={`border bg-surface p-2.5 select-none ${dragging ? "opacity-30" : overlay ? "shadow-2xl rotate-1" : "border-line/10 hover:border-line/30"}`}>
      <button type="button" onClick={onOpen} className="w-full text-left">
        <p className="text-[13px] font-semibold leading-snug break-words">{it.title}</p>
        <div className="mt-1.5 flex items-center justify-between text-[10px] text-fg/50">
          <span className="truncate">{who}</span>
          {it.updates?.length > 0 && <span className="flex items-center gap-0.5"><MessageSquare className="w-3 h-3" />{it.updates.length}</span>}
        </div>
      </button>
    </div>
  );
}
function KDraggable(props) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: props.it.id });
  return <div ref={setNodeRef} {...attributes} {...listeners} className="touch-none"><KCard {...props} dragging={isDragging} /></div>;
}
function KColumn({ col, items, board, users, onOpen }) {
  const { setNodeRef, isOver } = useDroppable({ id: `k|${col.id}` });
  return (
    <div ref={setNodeRef} data-testid={`kcol-${col.id || "none"}`} className={`min-w-[240px] w-[240px] shrink-0 border ${isOver ? "border-brand/60 bg-brand/5" : "border-line/10 bg-surface-2/40"}`}>
      <div className="px-2.5 py-2 border-b border-line/10 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full" style={{ background: col.color }} />
        <span className="text-xs font-semibold">{col.label}</span>
        <span className="ml-auto text-[10px] mono text-fg/45">{items.length}</span>
      </div>
      <div className="p-2 space-y-2 min-h-[56px]">
        {items.map((it) => <KDraggable key={it.id} it={it} board={board} users={users} onOpen={() => onOpen(it.id)} />)}
        {items.length === 0 && <div className="h-10 border border-dashed border-line/10" />}
      </div>
    </div>
  );
}
function KanbanView({ board, items, users, column, onMove, onOpen }) {
  const { columns, cells } = kanbanFor(items, column);
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor));
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  if (!column) return <div className="p-10 text-center text-sm text-fg/40">Board-ul n-are nicio coloană de tip Status — adaugă una ca să ai kanban.</div>;
  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={({ active }) => setActiveId(active.id)}
      onDragCancel={() => setActiveId(null)}
      onDragEnd={({ active, over }) => {
        setActiveId(null);
        if (!over) return;
        const target = String(over.id).slice(2);
        const it = byId.get(active.id);
        if (!it || (it.values?.[column.id] || "") === target) return;
        onMove(it, column, target || null);
      }}>
      <div className="h-full overflow-auto p-4" data-testid="board-kanban">
        <div className="flex gap-3 items-start">
          {columns.map((c) => <KColumn key={c.id || "none"} col={c} items={cells[c.id]} board={board} users={users} onOpen={onOpen} />)}
        </div>
      </div>
      <DragOverlay dropAnimation={null}>{activeId && byId.get(activeId) ? <div className="w-[224px]"><KCard it={byId.get(activeId)} board={board} users={users} overlay onOpen={() => {}} /></div> : null}</DragOverlay>
    </DndContext>
  );
}

// ---------------------------------------------------------------------------
// Item drawer
// ---------------------------------------------------------------------------
function ItemDrawer({ board, item, users, currentUser, onClose, onCell, onTitle, onGroup, onDelete, onUpdate }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    try { await onUpdate(item, text.trim()); setText(""); } finally { setBusy(false); }
  };
  return (
    <aside className="w-[380px] shrink-0 border-l border-line/10 bg-bg flex flex-col min-h-0" data-testid="item-drawer">
      <div className="h-14 px-4 flex items-center gap-2 border-b border-line/10">
        <EditableTitle value={item.title} canEdit onSave={(v) => onTitle(item, v)} testId="drawer-title" />
        <button type="button" onClick={() => setConfirm(true)} className="ml-auto p-1.5 text-fg/40 hover:text-red-400" aria-label="Șterge elementul"><Trash2 className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={onClose} className="p-1.5 text-fg/40 hover:text-fg" aria-label="Închide"><X className="w-3.5 h-3.5" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="space-y-2">
          <label className="label-tiny">Grup</label>
          <select value={item.group_id} onChange={(e) => onGroup(item, e.target.value)} className="input-flat text-xs py-1">
            {board.groups.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
          </select>
        </div>
        {board.columns.map((c) => (
          <div key={c.id} className="space-y-1">
            <label className="label-tiny">{c.label}</label>
            <div className="border border-line/10 min-h-[34px]"><BoardCell column={c} value={item.values?.[c.id]} users={users} onChange={(v) => onCell(item, c, v)} multiline /></div>
          </div>
        ))}
        <div className="border-t border-line/10 pt-3 space-y-2">
          <span className="label-tiny">Actualizări</span>
          {(item.updates || []).length === 0 && <p className="text-xs text-fg/40 italic">Nicio actualizare încă.</p>}
          {(item.updates || []).map((u) => (
            <div key={u.id} className="bg-surface-2 border border-line/10 p-2">
              <div className="flex items-center gap-2 text-[10px] text-fg/50 mb-1"><span className="font-semibold text-fg/80">{u.user_name}</span><span className="mono">{dateTime(u.created_at)}</span></div>
              <p className="text-xs whitespace-pre-wrap break-words">{u.content}</p>
            </div>
          ))}
        </div>
      </div>
      <form onSubmit={send} className="p-3 border-t border-line/10 flex gap-2">
        <input data-testid="update-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrie o actualizare…" className="input-flat text-xs py-1.5 flex-1" />
        <button type="submit" disabled={busy || !text.trim()} className="btn-primary text-xs px-3 py-1.5 disabled:opacity-50">Trimite</button>
      </form>
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} title="Ștergi elementul?" description={`«${item.title}» dispare de pe board pentru toată lumea.`}
        confirmLabel="Șterge" destructive onConfirm={() => { setConfirm(false); onDelete(item); }} testId="confirm-delete-item" />
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Column editor (add / rename / options / delete)
// ---------------------------------------------------------------------------
function ColumnEditor({ column, onSave, onDelete, onClose }) {
  const isNew = !column;
  const [label, setLabel] = useState(column?.label || "");
  const [type, setType] = useState(column?.type || "status");
  const [hiddenInTable, setHiddenInTable] = useState(column?.hidden_in_table === true);
  const [options, setOptions] = useState(column?.options || [{ label: "Neînceput", color: "#c4c4c4" }, { label: "În lucru", color: "#fdab3d" }, { label: "Gata", color: "#00c875" }]);
  const [confirm, setConfirm] = useState(false);
  const save = () => {
    const col = { ...(column || {}), label: label.trim(), type, hidden_in_table: hiddenInTable };
    if (type === "status") col.options = options.filter((o) => o.label.trim());
    if (!col.label) return;
    onSave(col);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onMouseDown={onClose} data-testid="column-editor">
      <div onMouseDown={(e) => e.stopPropagation()} className="w-full max-w-md bg-surface border border-line/10 shadow-xl">
        <div className="px-4 py-3 border-b border-line/10 font-bold tracking-tight">{isNew ? "Coloană nouă" : `Coloana «${column.label}»`}</div>
        <div className="p-4 space-y-3">
          <label className="block space-y-1"><span className="label-tiny">Nume</span>
            <input autoFocus data-testid="column-label" value={label} onChange={(e) => setLabel(e.target.value)} className="input-flat text-sm py-1.5 w-full" /></label>
          <label className="block space-y-1"><span className="label-tiny">Tip</span>
            <select data-testid="column-type" value={type} disabled={!isNew} onChange={(e) => setType(e.target.value)} className="input-flat text-sm py-1.5 w-full disabled:opacity-60">
              {COLUMN_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            {!isNew && <span className="text-[10px] text-fg/40">Tipul nu se schimbă după creare — valorile existente n-ar mai avea sens.</span>}
          </label>
          <label className="flex items-start gap-2 text-xs">
            <input type="checkbox" checked={hiddenInTable} onChange={(e) => setHiddenInTable(e.target.checked)} />
            <span>Doar în detaliile tichetului<span className="block text-fg/60 mt-1">Câmpul rămâne salvat, disponibil la căutare și în export. Se ascunde doar din tabel.</span></span>
          </label>
          {type === "status" && (
            <div className="space-y-1">
              <span className="label-tiny">Opțiuni</span>
              {options.map((o, i) => (
                <div key={o.id || i} className="flex items-center gap-2">
                  <input type="color" value={o.color} onChange={(e) => setOptions((os) => os.map((x, j) => j === i ? { ...x, color: e.target.value } : x))} className="w-7 h-7 p-0 border-0 bg-transparent" aria-label="Culoare" />
                  <input value={o.label} onChange={(e) => setOptions((os) => os.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="input-flat text-xs py-1 flex-1" />
                  <button type="button" onClick={() => setOptions((os) => os.filter((_, j) => j !== i))} className="p-1 text-fg/40 hover:text-red-400" aria-label="Șterge opțiunea"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <button type="button" onClick={() => setOptions((os) => [...os, { label: "", color: "#579bfc" }])} className="text-xs text-fg/50 hover:text-fg flex items-center gap-1"><Plus className="w-3 h-3" /> opțiune</button>
            </div>
          )}
          <div className="flex justify-between pt-2">
            {!isNew ? <button type="button" onClick={() => setConfirm(true)} className="text-xs text-red-400 hover:text-red-300">Șterge coloana</button> : <span />}
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="btn-ghost text-sm px-3 py-1.5">Anulează</button>
              <button type="button" data-testid="column-save" onClick={save} className="btn-primary text-sm px-3 py-1.5">Salvează</button>
            </div>
          </div>
        </div>
      </div>
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} title="Ștergi coloana?" description="Valorile din ea dispar de pe toate elementele. Nu se poate anula."
        confirmLabel="Șterge" destructive onConfirm={() => { setConfirm(false); onDelete(column); }} testId="confirm-delete-column" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function BoardPage({ boardId, workspaceId, currentUser, users = [], myRole = "member", subscribe, onBack, onAskAtena, standalone = false }) {
  const [board, setBoard] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setViewState] = useState(() => pref(`vs_board_view_${boardId}`, "table"));
  const [kanbanColId, setKanbanColId] = useState(() => pref(`vs_board_kcol_${boardId}`, ""));
  const [search, setSearch] = useState("");
  const [drawerId, setDrawerId] = useState(null);
  const [columnEditor, setColumnEditor] = useState(null);   // null | { column|null }
  const [confirmDeleteBoard, setConfirmDeleteBoard] = useState(false);
  const boardRef = useRef(null); boardRef.current = board;

  const setView = (v) => { setViewState(v); remember(`vs_board_view_${boardId}`, v); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/boards/${boardId}`);
      setBoard(data.board); setItems(data.items);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Board-ul nu s-a putut încărca");
      onBack?.();
    } finally { setLoading(false); }
  }, [boardId, onBack]);
  useEffect(() => { load(); }, [load]);

  // Live: every change anyone makes to this board lands here.
  useEffect(() => {
    if (!subscribe) return undefined;
    return subscribe((ev) => {
      if (ev.type === "board_upsert" && ev.board?.id === boardId) setBoard((b) => ({ ...(b || {}), ...ev.board }));
      else if (ev.type === "board_deleted" && ev.board_id === boardId) { toast.info("Board-ul a fost șters"); onBack?.(); }
      else if (ev.board_id !== boardId) return;
      else if (ev.type === "board_item_upsert") setItems((list) => list.some((i) => i.id === ev.item.id) ? list.map((i) => i.id === ev.item.id ? ev.item : i) : [...list, ev.item]);
      else if (ev.type === "board_item_deleted") { setItems((list) => list.filter((i) => i.id !== ev.item_id)); setDrawerId((d) => d === ev.item_id ? null : d); }
      else if (ev.type === "board_reordered") setItems((list) => list.map((i) => { const k = ev.item_ids.indexOf(i.id); return k >= 0 ? { ...i, group_id: ev.group_id, order: k } : i; }));
    });
  }, [subscribe, boardId, onBack]);

  const canSchema = canEditSchema(board, currentUser, myRole);
  const statusCols = useMemo(() => (board?.columns || []).filter((c) => c.type === "status"), [board]);
  const kanbanCol = statusCols.find((c) => c.id === kanbanColId) || statusCols[0] || null;
  const visible = useMemo(() => board ? searchItems(board, items, search, users) : [], [board, items, search, users]);
  const drawerItem = drawerId ? items.find((i) => i.id === drawerId) : null;

  // ---- mutations: optimistic where the user can see it land, always echoed
  const patchItem = async (it, body, optimistic) => {
    if (optimistic) setItems((list) => list.map((i) => i.id === it.id ? { ...i, ...optimistic } : i));
    try {
      const { data } = await api.patch(`/boards/${boardId}/items/${it.id}`, body);
      setItems((list) => list.map((i) => i.id === it.id ? data : i));
    } catch (e) {
      if (optimistic) setItems((list) => list.map((i) => i.id === it.id ? it : i));
      toast.error(e?.response?.data?.detail || "Nu s-a salvat");
    }
  };
  const onCell = (it, col, v) => patchItem(it, { values: { [col.id]: v } }, { values: { ...(it.values || {}), [col.id]: v } });
  const onTitle = (it, v) => patchItem(it, { title: v }, { title: v });
  const onGroup = (it, gid) => patchItem(it, { group_id: gid }, { group_id: gid });
  const onMove = (it, col, optId) => onCell(it, col, optId);
  const onAdd = async (groupId, title) => {
    try {
      const { data } = await api.post(`/boards/${boardId}/items`, { title, group_id: groupId });
      setItems((list) => list.some((i) => i.id === data.id) ? list : [...list, data]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Nu s-a adăugat"); throw e; }
  };
  const onDelete = async (it) => {
    setDrawerId(null);
    try { await api.delete(`/boards/${boardId}/items/${it.id}`); setItems((list) => list.filter((i) => i.id !== it.id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Nu s-a șters"); }
  };
  const onUpdate = async (it, content) => {
    try {
      const { data } = await api.post(`/boards/${boardId}/items/${it.id}/updates`, { content });
      setItems((list) => list.map((i) => i.id === it.id ? data : i));
    } catch (e) { toast.error(e?.response?.data?.detail || "Nu s-a trimis"); throw e; }
  };
  const patchBoard = async (body) => {
    try { const { data } = await api.patch(`/boards/${boardId}`, body); setBoard(data); return true; }
    catch (e) { toast.error(e?.response?.data?.detail || "Nu s-a salvat"); return false; }
  };
  const saveColumn = async (col) => {
    const cols = board.columns.some((c) => c.id === col.id) ? board.columns.map((c) => c.id === col.id ? col : c) : [...board.columns, col];
    if (await patchBoard({ columns: cols })) setColumnEditor(null);
  };
  const deleteColumn = async (col) => { if (await patchBoard({ columns: board.columns.filter((c) => c.id !== col.id) })) setColumnEditor(null); };
  const onRenameGroup = (g, label) => patchBoard({ groups: board.groups.map((x) => x.id === g.id ? { ...x, label } : x) });
  const onDeleteGroup = (g) => patchBoard({ groups: board.groups.filter((x) => x.id !== g.id) });
  const onAddGroup = () => patchBoard({ groups: [...board.groups, { label: `Grup ${board.groups.length + 1}` }] });
  const deleteBoard = async () => {
    try { await api.delete(`/boards/${boardId}`); onBack?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Nu s-a șters"); }
  };
  const exportCsv = () => {
    const blob = new Blob([toCsv(board, items, users)], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${board.name}.csv`; a.click(); URL.revokeObjectURL(a.href);
  };
  const openWindow = () => window.open(`${window.location.origin}/?board=${boardId}&window=1`, "_blank", "noopener,width=1400,height=900");

  if (loading && !board) return <div className="p-6 text-sm text-fg/50 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Se încarcă…</div>;
  if (!board) return null;

  return (
    <div className="h-full flex min-h-0 min-w-0" data-testid="board-page">
      <div className="flex-1 flex flex-col min-h-0 min-w-0">
        <div className="min-h-14 px-4 py-2 flex items-center gap-3 border-b border-line/10 bg-bg flex-wrap">
          {!standalone && <button type="button" onClick={onBack} className="p-1.5 text-fg/50 hover:text-fg" aria-label="Înapoi la board-uri"><ArrowLeft className="w-4 h-4" /></button>}
          <EditableTitle value={board.name} canEdit={canSchema} onSave={(v) => patchBoard({ name: v })} testId="board-title" />
          <span className="text-fg/40 text-xs mono">{items.length}</span>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-fg/40" />
              <input data-testid="board-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Caută…" className="input-flat text-xs py-1 pl-7 w-44" />
            </div>
            <div className="flex items-center border border-line/20" role="group" aria-label="Vizualizare">
              <button type="button" data-testid="view-table" onClick={() => setView("table")} aria-pressed={view === "table"} title="Tabel" className={`px-2 py-1.5 ${view === "table" ? "bg-brand/15 text-brand" : "text-fg/60 hover:text-fg"}`}><Table2 className="w-3.5 h-3.5" /></button>
              <button type="button" data-testid="view-kanban" onClick={() => setView("kanban")} aria-pressed={view === "kanban"} title="Kanban" className={`px-2 py-1.5 border-l border-line/20 ${view === "kanban" ? "bg-brand/15 text-brand" : "text-fg/60 hover:text-fg"}`}><KanbanSquare className="w-3.5 h-3.5" /></button>
            </div>
            {view === "kanban" && statusCols.length > 1 && (
              <select value={kanbanCol?.id || ""} onChange={(e) => { setKanbanColId(e.target.value); remember(`vs_board_kcol_${boardId}`, e.target.value); }} className="input-flat text-xs py-1" aria-label="Coloana de status">
                {statusCols.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            )}
            {canSchema && <button type="button" data-testid="add-column" onClick={() => setColumnEditor({ column: null })} className="text-xs px-2 py-1.5 border border-line/20 text-fg/70 hover:text-fg flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Coloană</button>}
            {canSchema && board.columns.some((c) => c.hidden_in_table) && (
              <details className="relative text-xs" data-testid="detail-fields-menu">
                <summary className="cursor-pointer px-2 py-1.5 border border-line/20">Câmpuri în detalii ({board.columns.filter((c) => c.hidden_in_table).length})</summary>
                <div className="absolute right-0 top-full z-30 w-56 bg-surface border border-line/20 shadow-xl p-1">
                  {board.columns.filter((c) => c.hidden_in_table).map((c) => (
                    <button type="button" key={c.id} onClick={(e) => { e.currentTarget.closest("details").open = false; setColumnEditor({ column: c }); }} className="w-full px-2 py-2 text-left hover:bg-surface-2">{c.label}</button>
                  ))}
                </div>
              </details>
            )}
            {onAskAtena && <button type="button" data-testid="board-ask-atena" onClick={onAskAtena} className="p-1.5 border border-line/20 text-fg/60 hover:text-brand" title="Întreabă Atena despre board-ul acesta"><Sparkles className="w-3.5 h-3.5" /></button>}
            <button type="button" onClick={exportCsv} className="p-1.5 border border-line/20 text-fg/60 hover:text-fg" title="Export CSV"><Download className="w-3.5 h-3.5" /></button>
            {!standalone && <button type="button" data-testid="open-window" onClick={openWindow} className="p-1.5 border border-line/20 text-fg/60 hover:text-fg" title="Deschide în fereastră separată"><ExternalLink className="w-3.5 h-3.5" /></button>}
            {canSchema && <button type="button" onClick={() => setConfirmDeleteBoard(true)} className="p-1.5 border border-line/20 text-fg/40 hover:text-red-400" title="Șterge board-ul"><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>
        </div>
        <div className="flex-1 min-h-0">
          {view === "kanban"
            ? <KanbanView board={board} items={visible} users={users} column={kanbanCol} onMove={onMove} onOpen={setDrawerId} />
            : <TableView board={board} items={visible} users={users} canSchema={canSchema} activeId={drawerId}
                onCell={onCell} onTitle={onTitle} onAdd={onAdd} onOpenItem={setDrawerId}
                onRenameGroup={onRenameGroup} onDeleteGroup={onDeleteGroup} onAddGroup={onAddGroup}
                onColumnMenu={(c) => setColumnEditor({ column: c })} />}
        </div>
      </div>
      {drawerItem && <ItemDrawer board={board} item={drawerItem} users={users} currentUser={currentUser} onClose={() => setDrawerId(null)}
        onCell={onCell} onTitle={onTitle} onGroup={onGroup} onDelete={onDelete} onUpdate={onUpdate} />}
      {columnEditor && <ColumnEditor column={columnEditor.column} onSave={saveColumn} onDelete={deleteColumn} onClose={() => setColumnEditor(null)} />}
      <ConfirmDialog open={confirmDeleteBoard} onOpenChange={setConfirmDeleteBoard} title="Ștergi board-ul?" description={`«${board.name}» și toate cele ${items.length} elemente dispar pentru toată lumea. Nu se poate anula.`}
        confirmLabel="Șterge board-ul" destructive onConfirm={deleteBoard} testId="confirm-delete-board" />
    </div>
  );
}
